import type { QueryClient, QueryKey } from '@tanstack/react-query'

import { applyControlLockState, subscribeControlLockDenied } from '../controlToken.ts'
import { keys } from '../keys.ts'

/**
 * 구독 이벤트 → 캐시 **무효화**.
 *
 * 🔴 **덮어쓰기가 아니다.** 유혹은 있다 — AsyncAPI 가 `batch_status` 페이로드는 `Batch` 동형,
 *    `module_status` 는 `ModuleSnapshot` 동형(=`allowed_commands` 포함)이라 그대로 캐시에 넣으면
 *    왕복이 준다. 그런데 셋이 걸린다.
 *      1. **부분 병합 사고를 부른다.** 일부 필드만 오는 순간 누군가 `{...old, ...new}` 를 쓴다.
 *      2. **version 역행.** 「더 높은 version 만 반영」(IFC-COM-06)과 `source_version` 비교 금지
 *         (DM-CONS-03)는 **서로 다른 두 축**이다. 헷갈리면 조용히 과거 상태를 그린다.
 *      3. 무효화는 서버가 **항상 완전한 집합**을 다시 준다 — SSOT 원칙과 같은 방향이다.
 *
 * 🔴 **2단계(2026-09-21) — 「전부 무효화」에서 「바뀐 칸을 보고 가린다」로 옮겼다.** 1단계
 *    주석은 「이벤트가 너무 잦아 실제로 문제가 되면 그때 재고 옮긴다」였고, **재 보니 문제가
 *    맞았다**: 배치가 도는 동안 `module_status` 가 모듈당 1Hz 로 오면서 `current_execution.step`
 *    만 바뀐 프레임이 `/batches` 를 **초당 한 번** 다시 긁었다(실측 75초에 152건).
 *    ⇒ :data:`BATCH_RELEVANT_MODULE_FIELDS` 에 든 칸이 바뀐 프레임만 배치를 긁는다.
 *
 * 🔴 **그래도 `setQueryData` 로 «승격»하지는 않았다 — 실물이 동형이 아니다**(2026-09-21 실측).
 *    `GET /modules` 항목과 `module_status.payload` 는 **키 20개가 정확히 같지만**
 *    `attribution` · `active_alarm_count` 두 칸의 **뜻이 다르다**. 구독 경로는 커밋 뒤
 *    발행이라 그 자리에 `conn` 이 없어 **판정을 안 하고 `null`(미산출)로 내보낸다**
 *    (`ctcells-core` `views/modules._UNJUDGED` · 백엔드 시험이 그 `null` 을 못 박는다).
 *    실측: 모듈 4대 전부 `active_alarm_count` 가 stream `null` · REST `0`.
 *    ⇒ 프레임을 캐시에 그대로 쓰면 **배지와 알람 수가 매초 지워진다.** 남은 두 칸만 옛 값에서
 *      끌어오는 것은 이 파일이 금지한 **부분 병합**이라 하지 않는다. 무효화로 남긴다.
 *    ⚠ 서버가 구독 경로에서도 그 둘을 채우는 날(백엔드 `_UNJUDGED` 가 사라지는 날)
 *      이 판단을 다시 연다 — `docs/backend-requests.csv` BR-56.
 *
 * 🔴 **`@ctcells/realtime` 은 쿼리 키를 모른다.** 그래서 이 매핑이 realtime 이 아니라 여기 있다.
 *    realtime 은 「연결 · stale · 원시 이벤트 emit」까지만 한다. 두 패키지가 서로를 모른다.
 *
 * ⚠ 무효화해도 **활성 관찰자가 없는 쿼리는 네트워크를 타지 않는다.** 알람 화면을 안 보고 있으면
 *   알람 이벤트가 와도 요청이 0건이다. 잘 맞는 성질이다.
 *
 * 🔴 **`batch_tracking`(서버 v0.9.4)도 덮어쓰지 않고 무효화한다 — 프레임이 스냅샷과 동형이 «아니다».**
 *    계약(`events.asyncapi.yaml` `L1EventStream.payload`)이 「진행률 칸 `percent`·`current_step` 은 싣지 않는다」고 적었고
 *    서버도 그 둘을 뺀 채 낸다(`ctcells-core` `services/batch_tracking.for_batch(with_progress=False)`). 그대로 쓰면 챔버의
 *    진행률이 사건마다 지워지고, 옛 값에서 끌어오면 위 1번(부분 병합)이다. ⇒ 그 배치의 추적 스냅샷을 다시 받는다.
 *    서버가 위치·상태를 바꾸는 사건이 커밋됐을 때만, 그것도 내용이 바뀌었을 때만 내므로 주기 조회가 되지 않는다(DP-01).
 */

/** `events.asyncapi.yaml` L1 이벤트 `type` 축 */
export type StreamEventType =
  | 'batch_progress'
  | 'batch_status'
  | 'module_status'
  | 'module_telemetry'
  | 'env_reading'
  | 'alarm'
  | 'connection'
  | 'sample_progress'
  | 'sample_lifecycle'
  | 'batch_tracking'

export interface StreamEventLike {
  type?: string
  /** 안전 스트림(`/safety/stream` · `SafetyStream`) 프레임만 싣는 필수 칸 — 이벤트 스트림과 가르는 표지다 */
  safety_state?: unknown
  batch_id?: string
  module_id?: string
  sample_id?: string
  payload?: unknown
}

/**
 * **REST 스냅샷으로 다시 맞출 자원** — 끊긴 동안의 사건은 다시 오지 않는다(서버 `stream_relay._connection` 「공백이 있었다는
 * 사실을 말하고, 클라이언트가 REST 스냅샷으로 재동기한다」). 장비 · 시료 · 작업 진행이 이 목록으로 다시 선다
 * (eric 2026-10-06 「장비랑 시료 프로세스.. 이벤트가 오면 처리하는데.. 중간중간에 빠졌어」).
 * 🔴 폴링이 아니다(DP-01) — 「다시 붙었다」는 한 번의 사실에만 한 번 다시 받는다(`useStreamResync` · 아래 `connection`).
 */
export function resyncQueryKeys(): QueryKey[] {
  return [
    keys.batches.all(),
    keys.samples.all(),
    keys.reports.all(),
    keys.modules.all(),
    keys.tracking.all(),
    /* 안전 상태 + 걸린 것 — 끊긴 동안의 E-STOP · 해제를 버튼이 놓치지 않는다(`safety.all()` 아래 둘) */
    keys.safety.all(),
    keys.alarms.all(),
    keys.monitoringTrays.all(),
  ]
}

/**
 * **모듈 스냅샷에서 「배치 버튼」이 보는 칸.** 🔴 **서버 코드가 정한 목록이다 — 늘리거나
 * 줄일 때는 그 근거를 같이 옮긴다.** 배치의 `allowed_commands` 는 `has_write_role` ∧
 * `holds_lock` ∧ 상태표 ∧ `preflight_passed` 넷을 보는데(`services/batches.allowed_commands`),
 * 그중 **모듈을 보는 것은 Pre-flight 와 제어락** 둘이다.
 *
 * | 칸 | 서버 근거 |
 * | --- | --- |
 * | `state` | `preflight._not_idle_blockers` — `IDLE` 이 아닌 모듈이 있으면 시작을 막는다 |
 * | `stale` | `_not_idle_blockers`·`_safety_blockers` 가 **같은** `modules.is_stale` 로 「모른다」를 가른다 |
 * | `hold` | `_safety_blockers` ⓒ — `hold.kind=safety_stop` 래치. ⚠ 비로봇 모듈은 안전정지를 **`state` 로 갖지 않는다**(`06`) |
 * | `consumables` | `_consumable_blockers` — 잔량 0 은 `fail` |
 * | `service_state` | 세 블로커 전부 `service_state=active` 인 모듈만 센다 |
 * | `e_stop` | 미러 자체는 Pre-flight 입력이 아니지만, 모듈 E-STOP 은 **같은 트랜잭션에서** 시스템 안전 상태를 옮긴다(`core/module_subscriber` → `services/safety.enter_emergency_stop`) → `_safety_blockers` ⓐ |
 * | `interlock` | 인터락 트립은 `category: critical` 이라 위와 **같은 경로**로 안전 상태를 옮긴다 |
 * | `allowed_commands` | 🔴 **제어락이 바뀐 것을 아는 유일한 신호**(`docs/backend-requests.csv` BR-55). 이 값은 배달부가 **구독자별**로 다시 그리므로(`views/events_stream.render_for`) 내 `holds_lock` 이 바뀌면 본문이 바뀐다 — 배치 `allowed_commands` 는 `holds_lock` 이 거짓이면 **빈 배열**이다 |
 *
 * 🔴 **여기 «없는» 칸이 1Hz 의 정체다** — `current_execution`(step·sequence) · `last_seen` ·
 *    `version` · `telemetry`. 배치 버튼은 그것들을 보지 않는다. 뺀 것이지 빠뜨린 것이 아니다.
 * ⚠ `control_owner` 도 없다 — 배치 게이트가 보는 락은 **L2 제어락 리스**
 *   (`services/control_lock.current`)이지 모듈의 제어권 표기가 아니다. 그 값이 바뀌면
 *   `allowed_commands` 가 따라 바뀌므로 위 칸이 이미 잡는다.
 * ⚠ `attribution`·`active_alarm_count` 도 없다 — 구독 경로는 **늘 `null`** 이라(위 머리 주석)
 *   견줄 값 자체가 없다.
 *
 * 🔴 **`state` 를 「`IDLE` 이냐」로 좁히지 마라 — 재 보고 «안 좁히기로» 정했다**(eric 확정
 *    2026-09-21). 실측(60초 창 · 브라우저가 받은 WS 프레임과 브라우저가 낸 요청을 같은
 *    monotonic 시계로 대조): `GET /batches` **19건 = `state` 전이 19건**, 짝 없는 요청 **0건** ·
 *    누락 **0건** · 시차 전부 −101ms. 이 걸름은 정확히 일한다(`current_execution` 은 57회
 *    바뀌고도 한 번도 못 긁었다). 다만 **그 19번이 받아 온 본문은 전부 같다**(해시 1가지) —
 *    원심의 집기/놓기가 `MOVING↔LOADING` 으로 매 초 오가는데 서버 배치 게이트는 `state` 를
 *    **`== IDLE` 이진으로만** 보기 때문이다(`services/preflight._not_idle_blockers` ·
 *    `_safety_blockers` 는 `state` 를 아예 안 읽는다).
 *    ⇒ 좁히면 오늘은 맞다. 그런데 **「이 state 값들은 배치에 같은 뜻」은 서버 규칙을 프론트가
 *      다시 유도하는 것**이고(「프론트는 상태를 추론하지 않는다」), 서버가 state 별 규칙을
 *      하나라도 더하면 **조용히 안 긁는다** — 붙일 수 있는 것을 못 붙인다고 믿게 하는
 *      「없는 차단」 부류다. 버는 것(로봇이 움직이는 ~20초에 같은 본문 18번 · 그 밖 40초는
 *      0건)이 그 위험보다 작다.
 *    ⚠ 이 목록은 **칸 목록**이다. 칸마다 「얼마나 바뀌어야 치나」를 넣는 것은 목록 편집이
 *      아니라 **칸별 투영자라는 새 장치**를 들이는 것이다.
 *    ⚠ **다시 열 조건은 둘뿐이다** — ⓐ 서버가 목록 DTO 에 `state` 별로 달라지는 값을 싣기
 *      시작한다 ⓑ 이 재조회가 **실제 부하로 관측된다**. 둘 다 아니면 그대로 둔다.
 */
export const BATCH_RELEVANT_MODULE_FIELDS: readonly string[] = [
  'state',
  'stale',
  'hold',
  'consumables',
  'service_state',
  'e_stop',
  'interlock',
  'allowed_commands',
]

/** 프레임에서 스냅샷 본문을 꺼낸다. 봉투에 `payload` 가 없으면 봉투 자신이 본문이다 */
function snapshotOf(event: Record<string, unknown>): Record<string, unknown> {
  const payload = event['payload']
  if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
    return payload as Record<string, unknown>
  }
  return event
}

/** 트레이 뷰를 다시 받는 모듈 걸음 — 집기 · 놓기 · 다음 자리로 출발(`module_status` 의 `current_execution.sequence`) */
export const TRAY_VIEW_STEP_SEQUENCES: ReadonlySet<string> = new Set(['grip', 'release', 'move_to'])

function trayStepOf(event: Record<string, unknown>): string | undefined {
  const execution = snapshotOf(event)['current_execution']
  if (typeof execution !== 'object' || execution === null) return undefined
  const sequence = (execution as Record<string, unknown>)['sequence']
  return typeof sequence === 'string' ? sequence : undefined
}

/** 배치에 닿는 칸만 뽑은 지문. 같으면 **배치 버튼이 바뀔 수 없다** */
function batchRelevantFingerprint(event: Record<string, unknown>): string {
  const body = snapshotOf(event)
  return JSON.stringify(BATCH_RELEVANT_MODULE_FIELDS.map((field) => body[field] ?? null))
}

/**
 * 이벤트 한 건이 무효화할 키. 모르는 `type` 은 **아무것도 안 한다** — 지어내지 않는다.
 *
 * 🔴 **`batch_progress` 는 여기 없다 — 뺀 것이지 빠뜨린 것이 아니다.** 그 갈래는 REST 에
 *    대응하는 값이 없어(`docs/backend-requests.csv` BR-09) 캐시를 비워도 서버가 다시 줄 것이
 *    없다. 그런데 실행 중 **1Hz** 로 와서 `/batches` 를 초당 한 번 다시 긁고 있었다
 *    (2026-09-21 실측 · DP-01 위반). 진행 축은 `progress.ts` 보관소가 받는다.
 *
 * @param previous 같은 자원의 **직전 프레임**. 없으면(첫 프레임·재연결 직후) 무엇이 바뀌었는지
 *   알 수 없으므로 **넓게 무효화한다** — 모르는 것을 「안 바뀌었다」로 접지 않는다.
 */
/**
 * **챔버 자리가 바뀌는 진행 프레임** — 실행 끝(완료 · 오류 · 취소)과, 챔버를 «내려놓거나» 옮기는 동작의 스텝 완료뿐이다(2026-10-07).
 * 🔴 스텝 완료 전부가 아니다 — 염색기는 스텝이 4천 개 넘게 거의 초마다 와서(묶음 창 100ms) 그대로 걸면 초당 조회가 된다(DP-01).
 *    동작 이름은 표준 레시피 원심 · 어셈블러 블록의 것(`placedown` 112 · `run_centrifugation` 6 · `slide_tray` 10 · 2026-10-07 셈).
 */
const CHAMBER_END_TYPES: ReadonlySet<string> = new Set([
  'complete',
  'error',
  'cancelled',
  'aborted',
])
const CHAMBER_MOVING_SEQUENCES: ReadonlySet<string> = new Set([
  'placedown',
  'run_centrifugation',
  'slide_tray',
])

function movesChambers(event: StreamEventLike): boolean {
  const payload = event.payload as
    | { type?: unknown; sequence?: unknown; result?: { sequence?: unknown } | null }
    | null
    | undefined
  if (typeof payload?.type !== 'string') return false
  if (CHAMBER_END_TYPES.has(payload.type)) return true
  if (payload.type !== 'step_complete') return false
  const sequence = payload.sequence ?? payload.result?.sequence
  return typeof sequence === 'string' && CHAMBER_MOVING_SEQUENCES.has(sequence)
}

/** 모듈이 분주 한 번을 마쳤다는 프레임인가 — `step_complete` + 시퀀스 이름이 `dispense` 로 시작 */
function isDispenseComplete(event: StreamEventLike): boolean {
  const payload = snapshotOf(event as Record<string, unknown>)
  if (payload['type'] !== 'step_complete') return false
  const sequence = payload['sequence']
  return typeof sequence === 'string' && sequence.startsWith('dispense')
}

export function invalidationKeysForEvent(
  event: StreamEventLike,
  previous?: StreamEventLike,
): QueryKey[] {
  /*
   * 🔴 **안전 스트림 프레임**(`safety_state` 가 실린 것 · 비상정지 · 인터락 · 해제 · 상태 전이)은 「걸린 것」만 다시 받는다 —
   *    시스템 안전 상태가 그 목록의 첫 항목이다(`services/stops.py` `safety`). 같은 내용의 되풀이는 다리의 지문이 거른다.
   * 🔴 **안전 상태 자체(`keys.safety.state()`)도 같이 다시 받는다**(2026-10-16 실측). 종전에는 걸린 것만 비워서, 다른 화면 · 다른
   *    단말에서 누른 E-STOP · 해제가 이 화면의 E-STOP 버튼(작동 중 반전 · `GET /safety`)에 새로고침 전까지 안 닿았다 —
   *    원심분리기 앱에서 누르고 API 로 해제한 뒤에도 버튼이 「작동 중」으로 남았고, 콘솔은 API 로 건 E-STOP 을 끝내 못 봤다.
   */
  if (event.safety_state !== undefined) return [keys.safety.state(), keys.safety.stops()]
  switch (event.type) {
    case 'batch_status':
      /*
       * 🔴 **시료 · 보고서도 같이**(2026-10-06) — 배치가 종착하면 서버가 그 배치의 시료를 같은 트랜잭션에서 바꾸는데
       *    (`services/batches` 의 시료 `status=discarded`) 시료 이벤트는 따로 안 낸다. 배치 상태만 다시 받으면 시료 카드 ·
       *    보고서가 다음 사건까지 옛 상태로 굳는다. 배치 상태 프레임은 드물어(전이마다 한 건) 값싸다.
       */
      return event.batch_id === undefined
        ? [
            keys.batches.all(),
            keys.samples.all(),
            keys.reports.all(),
            keys.monitoringTrays.all(),
            keys.safety.stops(),
          ]
        : [
            keys.batches.detail(event.batch_id),
            keys.safety.stops(),
            keys.batches.all(),
            keys.samples.all(),
            keys.reports.all(),
            keys.monitoringTrays.all(),
          ]
    case 'module_status': {
      const moduleKeys: QueryKey[] =
        event.module_id === undefined
          ? [keys.modules.all()]
          : [keys.modules.detail(event.module_id), keys.modules.all()]
      /**
       * 🔴 **모듈이 바뀌면 배치 버튼도 바뀐다 — 다만 «아무 칸이나»가 아니다.**
       *    어느 칸이 닿는지는 :data:`BATCH_RELEVANT_MODULE_FIELDS` 가 서버 근거와 함께 적는다.
       *    스텝이 한 칸 가는 것으로 `/batches` 를 다시 긁지 않는다.
       */
      if (
        previous === undefined ||
        batchRelevantFingerprint(event as Record<string, unknown>) !==
          batchRelevantFingerprint(previous as Record<string, unknown>)
      ) {
        moduleKeys.push(keys.batches.all())
        // 트레이 뷰도 같은 칸(상태 · 실행)에서 갈린다 — 심장박동으로는 다시 받지 않는다
        moduleKeys.push(keys.monitoringTrays.all())
        /*
         * 「걸린 것」(`GET /safety/stops`)도 같은 칸에서 갈린다 — 모듈 보류(`hold`) · 로봇 파지(`state`) · 비상정지 ·
         * 인터락이 그 목록의 항목이다(`ctcells-core` `services/stops.py`). 심장박동 · 스텝으로는 받지 않는다.
         */
        moduleKeys.push(keys.safety.stops())
      }
      /*
       * 🔴 **집기 · 놓기 · 다음 자리로 출발하는 걸음에서도 트레이 뷰를 다시 받는다**(eric 2026-10-08 「원심분리기에서 4개 중 1, 4번째는 C 트레이에서
       *    가져오는데 2, 3번째 챔버는 원심분리기 왔다갔다하면서 챔버가 생겨」). 상태 이름은 놓기(`LOADING`) → 올림 · 다음 챔버로 이동 · 내림 내내
       *    `MOVING` 이라 위 지문이 안 바뀌고, 다음 챔버를 집는 순간에도 화면의 트레이 뷰가 «앞 챔버를 놓은 직후» 판이었다 — 그 판에는 C 에서 다룰
       *    챔버가 없어 그리퍼가 C 로 가지 않았다. 걸음 하나에 한 번이 아니라 이 셋에서만이다(챔버 하나에 셋 · 심장박동으로는 받지 않는다)
       */
      const step = trayStepOf(event as Record<string, unknown>)
      if (
        step !== undefined &&
        TRAY_VIEW_STEP_SEQUENCES.has(step) &&
        (previous === undefined || step !== trayStepOf(previous as Record<string, unknown>))
      ) {
        moduleKeys.push(keys.monitoringTrays.all())
      }
      /*
       * ⚠ 6축 프레임으로 이송 여부를 다시 읽지 않는다(서버 v0.9.4) — 「이 배치의 트레이가 로봇 위 · 이송 중인가」는
       *   서버가 `batch_tracking` 프레임으로 직접 말한다(이송 시작 · 집음 · 놓음이 커밋될 때). 자원 점유는 걷혔다.
       */
      return moduleKeys
    }
    case 'module_telemetry':
      return event.module_id === undefined
        ? [keys.modules.all()]
        : [keys.modules.detail(event.module_id), keys.modules.all()]
    case 'env_reading':
      return [keys.envSensors.all()]
    case 'alarm':
      return [keys.alarms.all()]
    case 'sample_progress': {
      /*
       * 🔴 **진행률 프레임은 원자 하나마다 온다 — 그 시료의 보고서 하나만 다시 받는다**(2026-10-13 dev2 실측).
       *    서버 v0.9.4 는 원자가 끝날 때마다 낸다(`batch_dispatch.sample_progress_of` · 결정 C-108) — 염색 중 초당 1건.
       *    종전처럼 시료 전부(500건씩 쪽 넘김) · 보고서 전부 · 트레이 뷰를 매번 비우자 한 탭이 초당 열 건 넘게 긁어
       *    이 PC 에서만 10분에 1만 1천 건이 나갔고 API 컨테이너가 헬스 실패로 재시작됐다.
       *    이 프레임은 단계 표지(`sample_event`)를 쓰지 않는다 — 단계 · 상태는 `sample_lifecycle` 이 말한다(아래).
       *    보고서의 분주 기록(`dispenses`)만 원자마다 늘므로 그 시료 하나의 보고서만 비운다.
       * ⚠ `sample_id` 는 봉투가 아니라 `payload` 안에 있다(실측) — 둘 다 읽는다. 모르면 아무것도 안 한다.
       */
      const payload = event.payload as { sample_id?: unknown } | null | undefined
      const sampleId =
        event.sample_id ?? (typeof payload?.sample_id === 'string' ? payload.sample_id : undefined)
      return sampleId === undefined ? [] : [keys.reports.detail(sampleId)]
    }
    case 'sample_lifecycle':
      /*
       * 🔴 **보고서도 같이 무효화한다**(2026-09-22 실측 사고). 단계 표지가 찍히는 사건이
       *    곧 `sample_event` 한 줄이고, 그 줄을 읽는 창구가 `GET /reports/{id}` 다 —
       *    시료 목록만 무효화하면 **시료 집합이 안 바뀌는 한 보고서가 다시 오지 않는다**
       *    (캐시 키가 `keys.reports.detail(id)`). 실물에서 회차를 끝까지 돌렸는데 환자 카드의
       *    단계 시각이 **다섯 칸 모두 `—`** 였고, 새로고침해야만 찼다. 그 「새로고침해야 보임」을
       *    없애는 것이 구독의 일이다(DP-01 — 주기 폴링 없이).
       * ⚠ **`sample_id` 가 없다**(배치 단위 · eric 결정 2026-09-14). 그래서 도메인 전체를
       *   무효화한다 — 그 프레임은 배치당 4건뿐이라 값싸다.
       */
      return event.sample_id === undefined
        ? [keys.samples.all(), keys.reports.all(), keys.monitoringTrays.all()]
        : [
            keys.samples.detail(event.sample_id),
            keys.samples.all(),
            keys.reports.all(),
            keys.monitoringTrays.all(),
          ]
    case 'batch_progress':
      /*
       * 🔴 **분주 한 번이 끝났을 때만** 분주 기록을 다시 받는다(2026-09-28 · 챔버별 진행 `chamberProgress.ts`).
       *    분주 기록(`GET /dispense-records`)은 구독으로 안 오고(`module_subscriber.ingest_dispense` 가 DB 에만
       *    적는다) 웰마다 한 줄이라, 「어느 챔버까지 분주했나」를 알려면 다시 물어야 한다.
       * ⚠ 나머지 `batch_progress` 는 그대로 **아무것도 안 한다** — 1Hz 라 `/batches` 를 초당 긁던 사고가 있었다
       *   (위 머리말). 걸르는 칸은 모듈이 준 시퀀스 이름 하나다(실측 2026-09-28: 염색기 `dispense_reagent`
       *   `step_complete` 의 시각 = 분주 기록 `time` · 웰 하나당 한 번 · 약 8초 간격).
       */
      /*
       * 트레이 뷰는 챔버 자리가 바뀌는 프레임(`movesChambers`)에서만 다시 받는다(2026-10-07).
       * `progress` · `telemetry`(1Hz) · 염색 분주 스텝으로는 받지 않는다.
       */
      return [
        ...(isDispenseComplete(event) ? [keys.dispenseRecords.all()] : []),
        ...(movesChambers(event) ? [keys.monitoringTrays.all()] : []),
      ]
    case 'batch_tracking':
      /*
       * 🔴 **그 배치의 추적 스냅샷을 다시 받는다**(머리말 — 프레임에 진행률 칸이 없어 덮어쓰지 않는다).
       *    「이 배치가 6축 이송 중인가」(`trays[].where` 의 `robot` · `in_transit`)가 이 갈래로 선다.
       * ⚠ `batch_id` 가 없으면 도메인 전체 — 지어내지 않는다(서버는 늘 싣는다 · `views/batch_tracking.publish`).
       */
      /* 이송 격리 · 해제도 이 프레임으로 온다 — 「걸린 것」의 격리 항목(`services/stops.py` `quarantine`)을 같이 다시 받는다 */
      return event.batch_id === undefined
        ? [keys.tracking.all(), keys.safety.stops()]
        : [keys.tracking.detail(event.batch_id), keys.safety.stops()]
    case 'broker_change': {
      /*
       * 브로커 교체 행의 state · step 이 바뀌었다(`IFC-WS-03` · 계약 `BrokerAccountChange` 동형 · 비밀번호 칸 없음).
       * 계정 목록 · 모듈 접속을 다시 받는다 — 지우기 · 비밀번호 · 접속 교체가 관찰 뒤 끝나거나 되돌려질 때 화면이 따라온다.
       * 🔴 모듈 ID 바꾸기(`kind: identity`)는 끝날 때 레지스트리를 대체하므로 모듈 목록도 같이.
       */
      const payload = event.payload as { kind?: unknown } | null | undefined
      return payload?.kind === 'identity'
        ? [keys.broker.all(), keys.modules.all()]
        : [keys.broker.all()]
    }
    case 'connection': {
      /*
       * 🔴 서버의 모듈 링크(MQTT 등)가 **다시 붙었다**(`payload.connected: true`) — 끊긴 동안의 모듈 · 시료 사건은 다시 오지
       *    않으니 REST 로 다시 맞춘다(`resyncQueryKeys`). 끊김(`false`) 자체는 stale 판정의 입력이라 realtime 이 처리한다.
       */
      const payload = event.payload as { connected?: unknown } | null | undefined
      return payload?.connected === true ? resyncQueryKeys() : []
    }
    default:
      return []
  }
}

/**
 * **심장박동 판별에서 무시하는 칸.** 이 둘만 다른 프레임은 「살아 있다」를 되풀이한 것이지
 * 스냅샷이 바뀐 것이 아니다.
 *
 * 🔴 **재 보고 고른 두 칸이다.** 2026-09-21 로컬 실서버 `/events/stream` 60초: `module_status`
 *    238건(모듈 4대 × 1Hz)이 왔고, 같은 모듈의 연속 프레임 차이가 **`ts` 와 `payload.last_seen`
 *    뿐**이었다(4대 전부). 그 프레임마다 무효화가 나가 `/modules` 를 **초당 한 번** 다시 긁었다.
 * ⚠ 추측으로 늘리지 마라. 칸을 하나 더 넣는 것은 「그 칸이 바뀌어도 화면을 안 고친다」는 선언이다.
 */
const ENVELOPE_HEARTBEAT_KEY = 'ts'
const PAYLOAD_HEARTBEAT_KEY = 'last_seen'

/** 같은 자원의 프레임끼리만 견준다 — 종류 + 신원 */
function eventIdentity(event: StreamEventLike): string {
  const subject = event.module_id ?? event.batch_id ?? event.sample_id ?? ''
  return `${String(event.type)}|${subject}`
}

/**
 * 내용 지문. 같으면 **아무것도 안 바뀐 것**이다.
 *
 * ⚠ 칸 순서에 기댄다(같은 발행자가 같은 순서로 낸다). 순서가 흔들리면 지문이 갈려
 *   **무효화가 한 번 더 나갈 뿐**이다 — 틀리는 쪽이 아니라 도로 긁는 쪽으로 넘어진다.
 */
function contentFingerprint(event: Record<string, unknown>): string {
  const envelope: Record<string, unknown> = { ...event }
  delete envelope[ENVELOPE_HEARTBEAT_KEY]
  const payload = envelope['payload']
  if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
    const body: Record<string, unknown> = { ...(payload as Record<string, unknown>) }
    delete body[PAYLOAD_HEARTBEAT_KEY]
    envelope['payload'] = body
  }
  return JSON.stringify(envelope)
}

/**
 * 폭주 방지 — 키별로 모았다가 한 번에 무효화한다.
 *
 * ⚠ **폴링이 아니다.** 이벤트가 없으면 아무 일도 일어나지 않는다. 이벤트 구동이다.
 * ⚠ 창 크기는 설정값으로 받는다. 숫자를 코드에 박지 않는다.
 */
export interface StreamBridge {
  /** 원시 이벤트를 넣는다. 구독 계층이 `subscribe(name, push)` 로 연결한다 */
  push(event: unknown): void
  /** 대기 중인 무효화를 즉시 흘린다(테스트·화면 전환) */
  flush(): void
  dispose(): void
}

export function createStreamBridge(
  queryClient: QueryClient,
  options: {
    coalesceMs: number
    /**
     * 같은 키를 다시 무효화하기까지의 최소 간격(ms) — 0 이면 제한 없음.
     * 🔴 **2026-10-14 dev2 실측**: 모듈 4대가 상태를 1Hz 씩 내면 묶음 창(0.1초)마다 `/modules` 를 다시 받아
     *    15분에 5,028건(초당 5.6건)이 들어왔고, 그 시각 서버가 DB 쓰기 · 위치 등록부 조회 시간 초과를 내며
     *    새 작업의 첫 발송 응답을 놓쳐 «결과를 모르는 시도»로 멈췄다. 첫 프레임은 종전처럼 곧바로 흘리고,
     *    그 뒤 같은 키는 이 간격 끝에 «한 번» 모아 흘린다(마지막 상태는 늘 반영된다 · 주기 조회 아님 — 프레임이 없으면 0건).
     */
    minIntervalMs?: number
  },
): StreamBridge {
  const pending = new Map<string, QueryKey>()
  /** 키별 마지막으로 흘린 시각 — `minIntervalMs` 판정 */
  const flushedAt = new Map<string, number>()
  const minInterval = options.minIntervalMs ?? 0
  /**
   * 자원별 **직전 프레임**. 두 가지를 판정한다 — ⓐ 심장박동이냐(내용 지문) ⓑ 배치에 닿는 칸이
   * 바뀌었냐. 지문만 들고 있으면 ⓑ 를 못 재서 프레임 자체를 보관한다.
   */
  const lastFrame = new Map<string, { event: StreamEventLike; fingerprint: string }>()
  let timer: ReturnType<typeof setTimeout> | null = null

  /** 대기 중인 키를 흘린다 — `force` 가 아니면 최소 간격이 안 지난 키는 남겨 두고 그 간격 끝에 다시 본다 */
  function drain(force: boolean): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    const now = Date.now()
    let wait = Infinity
    for (const [id, key] of pending) {
      const last = flushedAt.get(id)
      const due = last === undefined ? now : last + minInterval
      if (!force && due > now) {
        wait = Math.min(wait, due - now)
        continue
      }
      pending.delete(id)
      flushedAt.set(id, now)
      void queryClient.invalidateQueries({ queryKey: key })
    }
    if (pending.size > 0 && wait !== Infinity) timer = setTimeout(() => drain(false), wait)
  }

  function flush(): void {
    drain(true)
  }

  function enqueue(invalidationKeys: readonly QueryKey[]): void {
    for (const key of invalidationKeys) {
      pending.set(JSON.stringify(key), key)
    }
    if (pending.size > 0 && timer === null) {
      timer = setTimeout(() => drain(false), options.coalesceMs)
    }
  }

  /**
   * 🔴 **409 `CONTROL_LOCK_HELD` 그물**(2026-09-21). 다른 클라이언트가 락을 잡거나 놓는 것을
   *    알려 주는 **구독 이벤트가 없다**(BR-55) — 그 사이 우리 캐시의 `allowed_commands` 는
   *    「눌러도 된다」로 남아 있다. 거절을 받은 순간이 그 사실을 아는 첫 자리이므로, 그때
   *    **권위를 다시 묻는다.** ⚠ BR-55 의 **대체가 아니라 그물**이다 — 눌러 봐야 안다.
   * ⚠ **`CONTROL_LOCK_HELD` 하나에만 걸린다.** 같은 409 라도 `BATCH_COMMAND_NOT_ALLOWED` 는
   *   사전이 「락을 얻어도 안 풀린다」고 못 박은 **다른 축**이다(`error_codes.yaml`). 거기 걸면 배치 전이가
   *   거절될 때마다 목록을 긁는다. 갈래는 `client.ts` 가 이미 가른다.
   */
  const offDenied = subscribeControlLockDenied(() => {
    enqueue([keys.batches.all(), keys.modules.all()])
  })

  return {
    push(event) {
      if (typeof event !== 'object' || event === null) return
      if ('type' in event && event.type === 'control_lock') {
        const frame = event as { payload?: unknown; ts?: string }
        if (applyControlLockState(frame.payload, undefined, frame.ts)) {
          enqueue([keys.batches.all(), keys.modules.all()])
        }
        return
      }

      /**
       * 🔴 **심장박동은 무효화하지 않는다.** 직전 프레임과 내용이 같으면 서버 스냅샷도
       *    그대로다 — 다시 긁어 봐야 같은 본문을 받는다. 이 걸름이 없으면 1Hz 심장박동이
       *    그대로 **초당 1회 REST** 가 된다(DP-01 이 금한 그 모양).
       * ⚠ 진짜로 바뀐 프레임은 지문이 달라 **즉시** 통과한다 — 늦추는 것이 아니다.
       */
      const identity = eventIdentity(event)
      const previous = lastFrame.get(identity)
      const fingerprint = contentFingerprint(event as Record<string, unknown>)
      if (previous?.fingerprint === fingerprint) return

      const invalidationKeys = invalidationKeysForEvent(event, previous?.event)
      // 🔴 **매핑이 없는 갈래도 직전 프레임으로 «기억»한다** — `batch_progress` 처럼 키가
      //    0개인 갈래에서 일찍 빠지면 지문이 안 쌓여 다음 프레임이 늘 「처음」이 된다.
      lastFrame.set(identity, { event, fingerprint })
      if (invalidationKeys.length === 0) return

      enqueue(invalidationKeys)
    },
    flush,
    dispose() {
      offDenied()
      if (timer !== null) clearTimeout(timer)
      timer = null
      pending.clear()
      flushedAt.clear()
      // 🔴 지문도 버린다 — 다시 붙었을 때 첫 프레임은 **반드시** 통과해야 한다
      //    (끊긴 동안 바뀐 것을 「같다」로 접으면 낡은 화면이 그대로 산다)
      lastFrame.clear()
    },
  }
}
